import { localStorageUtils, STORAGE_KEYS } from "../../../utils/localStorage";
import {
  normalizeWorkbookViewState,
  WorkbookViewState,
} from "./sheetViewState";

/**
 * Remembering a view between openings of the same file. Deliberately modest:
 * ten files, most recent first, through the same storage helper the rest of the
 * platform uses.
 *
 * Restoring is never silent - SheetView shows a banner naming what came back
 * and offering Reset. A person who opens a payroll file and sees 40 of 52,110
 * rows without being told has every reason to report it as data loss.
 */

const MAX_FILES = 10;
/** A hidden-row list longer than this is not worth a storage quota error. */
const MAX_PERSISTED_HIDDEN_ROWS = 5000;

interface PersistedEntry {
  savedAt: number;
  state: WorkbookViewState;
}

interface PersistedShape {
  v: 1;
  entries: Record<string, PersistedEntry>;
}

const EMPTY: PersistedShape = { v: 1, entries: {} };

function readAll(): PersistedShape {
  const parsed = localStorageUtils.getItem<PersistedShape>(
    STORAGE_KEYS.SHEET_VIEW,
    EMPTY,
  );
  if (!parsed || parsed.v !== 1 || typeof parsed.entries !== "object") {
    return { v: 1, entries: {} };
  }
  return parsed;
}

function trimHiddenRows(state: WorkbookViewState): WorkbookViewState {
  const bySheet: WorkbookViewState["bySheet"] = {};
  const ids = Object.keys(state.bySheet);
  for (let i = 0; i < ids.length; i++) {
    const sheet = state.bySheet[ids[i]];
    bySheet[ids[i]] =
      sheet.hiddenRows.length > MAX_PERSISTED_HIDDEN_ROWS
        ? { ...sheet, hiddenRows: [] }
        : sheet;
  }
  return { ...state, bySheet };
}

export function loadSheetView(fileKey: string): WorkbookViewState | null {
  if (!fileKey) return null;
  const entry = readAll().entries[fileKey];
  if (!entry) return null;
  // A view saved before freezing, column widths or the card mode existed is
  // missing those fields; normalising here keeps the pipeline off undefined.
  return normalizeWorkbookViewState(entry.state);
}

export function saveSheetView(fileKey: string, state: WorkbookViewState): void {
  if (!fileKey) return;
  const data = readAll();
  data.entries[fileKey] = { savedAt: Date.now(), state: trimHiddenRows(state) };

  const keys = Object.keys(data.entries);
  if (keys.length > MAX_FILES) {
    keys
      .sort((a, b) => data.entries[a].savedAt - data.entries[b].savedAt)
      .slice(0, keys.length - MAX_FILES)
      .forEach((key) => {
        delete data.entries[key];
      });
  }
  localStorageUtils.setItem(STORAGE_KEYS.SHEET_VIEW, data);
}

export function clearSheetView(fileKey: string): void {
  if (!fileKey) return;
  const data = readAll();
  if (!data.entries[fileKey]) return;
  delete data.entries[fileKey];
  localStorageUtils.setItem(STORAGE_KEYS.SHEET_VIEW, data);
}
